/**
 * Azure Application Insights — browser SDK init.
 *
 * Loaded once from main.tsx before <App /> renders. No-op when
 * VITE_APPINSIGHTS_CONNECTION_STRING is unset (local dev w/o
 * telemetry).
 *
 * Service identity for KQL queries:
 *   - cloud_RoleName       = "cdb-webos-frontend"
 *   - application_Version  = VITE_GIT_SHA (set at build time)
 *
 * PII sanitization: a TelemetryInitializer drops the most common PII
 * vectors (email patterns, Authorization headers) before the envelope
 * leaves the browser. Telemetry-emitting call sites should not place
 * raw user input into custom properties in the first place.
 */

import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// Read the connection string straight from window.__RUNTIME_CONFIG__
// (set by docker-entrypoint.sh at container start) with a fallback to
// import.meta.env for dev/test. We intentionally do NOT import from
// './env' here: that pulled env.ts into the same chunk graph as
// auth-config.ts, and the Vite production bundle started initializing
// auth-config's module-level const before env.ts had been evaluated,
// surfacing as `Cannot read properties of undefined (reading
// 'azureAdTenantId')` on login.
function readRuntimeString(key: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const runtimeValue = (typeof window !== 'undefined' ? (window as any).__RUNTIME_CONFIG__?.[key] : undefined) as
    | string
    | undefined;
  if (runtimeValue && !runtimeValue.startsWith('__') && !runtimeValue.endsWith('__')) {
    return runtimeValue;
  }
  return (import.meta.env[key] as string | undefined) ?? '';
}

export const reactPlugin = new ReactPlugin();

let _appInsights: ApplicationInsights | null = null;

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

function scrubString(value: string): string {
  return value.replace(EMAIL_RE, '[redacted-email]');
}

function scrubProperties(obj: unknown): void {
  if (!obj || typeof obj !== 'object') return;
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (key.toLowerCase() === 'authorization' || key.toLowerCase() === 'cookie') {
      record[key] = '[redacted]';
      continue;
    }
    if (typeof value === 'string') {
      record[key] = scrubString(value);
    } else if (value && typeof value === 'object') {
      scrubProperties(value);
    }
  }
}

function piiInitializer(envelope: ITelemetryItem): boolean {
  if (envelope.data) scrubProperties(envelope.data);
  if (envelope.baseData) scrubProperties(envelope.baseData);
  // Identify the role for KQL filtering (`cloud_RoleName`).
  envelope.tags = envelope.tags ?? {};
  (envelope.tags as Record<string, string>)['ai.cloud.role'] = 'cdb-webos-frontend';
  // Scrub any remaining string tag values.
  const tags = envelope.tags as Record<string, unknown>;
  for (const tagKey of Object.keys(tags)) {
    if (typeof tags[tagKey] === 'string') {
      tags[tagKey] = scrubString(tags[tagKey] as string);
    }
  }
  return true;
}

/**
 * Initialize the Application Insights browser SDK. Idempotent.
 * Returns the instance, or null when no connection string is configured.
 */
export function initAppInsights(): ApplicationInsights | null {
  if (_appInsights) return _appInsights;

  const connectionString = readRuntimeString('VITE_APPINSIGHTS_CONNECTION_STRING');
  if (!connectionString) {
    return null;
  }

  _appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      // Auto-capture unhandled errors + promise rejections.
      autoTrackPageVisitTime: true,
      disableTelemetry: import.meta.env.DEV,
      // Don't capture cookies in telemetry envelopes.
      isCookieUseDisabled: false,
      disableExceptionTracking: false,
      disableAjaxTracking: false,
    },
  });

  _appInsights.loadAppInsights();
  _appInsights.context.application.ver = (import.meta.env.VITE_GIT_SHA as string | undefined) || 'unknown';
  _appInsights.addTelemetryInitializer(piiInitializer);

  return _appInsights;
}
